import React, { useEffect, useRef, useState } from "react";
import { Typography } from "@mui/material";

const THUMB_MAX_WIDTH = 168;
const THUMB_MAX_HEIGHT = 220;

type PDFViewerThumbnailsProps = {
  classes: Record<string, string>;
  pdfDoc: any;
  totalPages: number;
  currentPage: number;
  onPageSelect: (pageNo: number) => void;
};

export const PDFViewerThumbnails = (props: PDFViewerThumbnailsProps) => {
  const { classes, pdfDoc, totalPages, currentPage, onPageSelect } = props;
  const canvasRefs = useRef<{ [page: number]: HTMLCanvasElement | null }>({});
  const [renderedPages, setRenderedPages] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (!pdfDoc || !totalPages) return;
    let cancelled = false;

    const renderThumbnails = async () => {
      for (let pageNo = 1; pageNo <= totalPages; pageNo += 1) {
        if (cancelled) break;
        const canvas = canvasRefs.current[pageNo];
        if (!canvas) continue;
        try {
          const page = await pdfDoc.getPage(pageNo);
          const baseViewport = page.getViewport({ scale: 1 });
          const scale = Math.min(
            THUMB_MAX_WIDTH / Math.max(baseViewport.width, 1),
            THUMB_MAX_HEIGHT / Math.max(baseViewport.height, 1)
          );
          const viewport = page.getViewport({ scale });
          const context = canvas.getContext("2d");
          if (!context) continue;
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          context.setTransform(1, 0, 0, 1, 0, 0);
          await page.render({ canvasContext: context, viewport }).promise;
          if (!cancelled) {
            setRenderedPages((prev) => ({ ...prev, [pageNo]: true }));
          }
        } catch {
          // Skip pages that fail thumbnail render.
        }
      }
    };

    renderThumbnails();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, totalPages]);

  return (
    <div className={classes.pdfSidebarPanelBody}>
      <Typography variant="subtitle2" className={classes.pdfSidebarTitle}>
        Pages
      </Typography>
      <div className={classes.pdfThumbnailList}>
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNo) => (
          <button
            key={`thumb-${pageNo}`}
            type="button"
            className={`${classes.pdfThumbnailItem} ${
              pageNo === currentPage ? classes.pdfThumbnailActive : ""
            }`}
            onClick={() => onPageSelect(pageNo)}
            aria-label={`Go to page ${pageNo}`}
            aria-current={pageNo === currentPage ? "page" : undefined}
          >
            <canvas
              ref={(el) => {
                canvasRefs.current[pageNo] = el;
              }}
              className={classes.pdfThumbnailCanvas}
            />
            <Typography variant="caption" className={classes.pdfThumbnailLabel}>
              {renderedPages[pageNo] ? pageNo : "…"}
            </Typography>
          </button>
        ))}
      </div>
    </div>
  );
};
